import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon, Loader } from "semantic-ui-react";

import { apirequest } from "../../../helpers/api-client";
import { t, tp } from "../../../constant/translations";
import { amountToCurrency, getAppCurrency } from "../../../helpers/currency";
import { formatDate } from "../../../helpers/date";
import { actionLabel } from "../../../helpers/contract-service";

const Wrap = styled.div`
  position: relative;
  padding-left: 6px;
`;

const Item = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 26px 1fr;
  gap: 12px;
  padding-bottom: 16px;
  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 12px;
    top: 28px;
    bottom: -2px;
    width: 2px;
    background: #e7e5e4;
  }
`;

const Mark = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #e7e5e4;
  z-index: 1;
  background: ${(props) =>
    props.action === "create"
      ? "#e8f8ee"
      : props.action === "destroy"
      ? "#fdecea"
      : props.action === "apply_scheduled"
      ? "#eaf2fd"
      : "#fdf3e2"};
  color: ${(props) =>
    props.action === "create"
      ? "#1f9a52"
      : props.action === "destroy"
      ? "#c0432f"
      : props.action === "apply_scheduled"
      ? "#1d6ae0"
      : "#b9740a"};
`;

const Body = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  &:hover .chev {
    color: #1c1917;
  }
`;

const Text = styled.div`
  font-size: 14px;
  & b {
    font-weight: 700;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  color: #78716c;
  font-size: 12.5px;
`;

const Chev = styled.span`
  color: #78716c;
  font-size: 12px;
  margin-left: 8px;
`;

const Diffs = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  background: #fafbfc;
  border: 1px solid #f1f2f4;
  border-radius: 8px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

const DiffRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
  padding: 4px 0;
  & .k {
    color: #78716c;
    text-transform: uppercase;
    font-size: 10.5px;
    letter-spacing: 0.5px;
    font-weight: 700;
    align-self: center;
  }
  & .v {
    color: #1c1917;
  }
  & .old {
    color: #78716c;
    text-decoration: line-through;
    margin-right: 8px;
  }
`;

const Empty = styled.div`
  color: #78716c;
  font-size: 13.5px;
  padding: 14px;
  border: 1px dashed #e7e5e4;
  border-radius: 10px;
  text-align: center;
`;

function iconFor(action) {
  if (action === "create") return "plus";
  if (action === "destroy") return "trash";
  if (action === "apply_scheduled") return "check";
  return "edit";
}

const FIELD_LABEL_KEYS = {
  name: "contract.service_name",
  category: "contract.category",
  amount: "contract.line.amount_per_month",
  start_date: "contract.line.effective_from",
  end_date: "contract.line.end_label",
  notes: "contract.notes",
  scheduled_change: "contract.line.scheduled_title",
  applied_change: "contract.line.scheduled_title",
};

function fmtVal(field, value) {
  if (value == null || value === "") return "—";
  if (field === "amount") {
    return amountToCurrency(value, getAppCurrency(), getAppCurrency());
  }
  if (field === "start_date" || field === "end_date") {
    return formatDate(value);
  }
  if (field === "scheduled_change" || field === "applied_change") {
    if (typeof value !== "object") return String(value);
    if (value.type === "end") {
      return `${t("contract.line.change_type_end")} · ${formatDate(
        value.effective_from
      )}`;
    }
    const amt = value.new_amount
      ? amountToCurrency(value.new_amount, getAppCurrency(), getAppCurrency())
      : "";
    const pct =
      value.type === "pct" && value.value != null
        ? ` (${parseFloat(value.value) >= 0 ? "+" : ""}${value.value}%)`
        : "";
    return `${amt}${pct} · ${formatDate(value.effective_from)}`;
  }
  return String(value);
}

function titleFor(event) {
  const action = event.action;
  if (action === "create") return t("contract.line.action_create");
  if (action === "destroy") return t("contract.line.action_destroy");
  if (action === "apply_scheduled") return t("contract.line.action_applied");
  const changes = event.changes || [];
  if (changes.length === 1) {
    const f = changes[0].field;
    if (f === "amount") return t("contract.line.action_price_corrected");
    if (f === "scheduled_change") {
      return changes[0].after == null
        ? t("contract.line.action_scheduled_cleared")
        : t("contract.line.action_scheduled");
    }
  }
  const schedChange = changes.find((c) => c.field === "scheduled_change");
  if (schedChange) {
    return schedChange.after == null
      ? t("contract.line.action_scheduled_cleared")
      : t("contract.line.action_scheduled");
  }
  return actionLabel(action);
}

const HistoryEntry = ({ event }) => {
  const [open, setOpen] = useState(false);
  const changes = (event.changes || []).filter(
    (c) => c.field !== "__destroyed__"
  );
  const title = titleFor(event);
  const userName =
    (event.user && event.user.name) || t("contract.line.system_user");
  const canExpand = changes.length > 0;

  return (
    <Item>
      <Mark action={event.action}>
        <Icon name={iconFor(event.action)} style={{ margin: 0 }} />
      </Mark>
      <Body>
        <Header
          onClick={() => canExpand && setOpen((o) => !o)}
          style={{ cursor: canExpand ? "pointer" : "default" }}
        >
          <div>
            <Text>
              <b>{title}</b>
              {canExpand && (
                <Chev className="chev">
                  ·{" "}
                  {changes.length === 1
                    ? t("contract.line.changes_one")
                    : tp("contract.line.changes_many", { n: changes.length })}
                </Chev>
              )}
            </Text>
            <Meta>
              {userName} · {formatDate(event.created_at)}
            </Meta>
          </div>
          {canExpand && (
            <Icon
              name={open ? "chevron up" : "chevron down"}
              style={{ color: "#78716c" }}
            />
          )}
        </Header>
        {open && canExpand && (
          <Diffs>
            {changes.map((c) => (
              <DiffRow key={c.field}>
                <span className="k">
                  {t(FIELD_LABEL_KEYS[c.field] || c.field)}
                </span>
                <span className="v">
                  {c.before != null && c.before !== "" && (
                    <span className="old">{fmtVal(c.field, c.before)}</span>
                  )}
                  {fmtVal(c.field, c.after)}
                </span>
              </DiffRow>
            ))}
          </Diffs>
        )}
      </Body>
    </Item>
  );
};

export const HistoryTimeline = ({ serviceId }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setRows(null);
    setError(null);
    apirequest("get", `/api/contracted-service/${serviceId}/history/`).end(
      (err, res) => {
        if (!active) return;
        if (err) {
          setError(t("contract.line.history_load_error"));
          return;
        }
        setRows(res.body || []);
      }
    );
    return () => {
      active = false;
    };
  }, [serviceId]);

  if (rows === null && !error) return <Loader active inline="centered" />;
  if (error) return <Empty>{error}</Empty>;
  if (!rows.length) return <Empty>{t("contract.line.scheduled_empty")}</Empty>;

  return (
    <Wrap>
      {rows.map((e) => (
        <HistoryEntry key={e.id} event={e} />
      ))}
    </Wrap>
  );
};
